import { ServicesContainer } from '@/services';
import { AccountPageLoadEvent } from '@/services/analytics/events/';
import { AutoAccrualTrackingType, PageLoadEventType, WebPageDetails } from '@/services/analytics/platforms/adobe-analytics/models';
import { AppState } from '@/store';
import { Store } from 'vuex';
import { AdobePageLoadEvent } from './adobe-page-load';

export class AdobeAccountPageLoadEvent extends AdobePageLoadEvent {
  constructor(event: AccountPageLoadEvent, store: Store<AppState>, services: ServicesContainer) {
    super(store, services);

    this.web.webPageDetails.pageType = this.web.webPageDetails.siteSection = 'account';

    Object.assign(this.web.webPageDetails, this.formatAccountPageDetails(event));
  }

  // format the web page details according to the account page event type.
  formatAccountPageDetails(event: AccountPageLoadEvent): WebPageDetails {
    const eventType = event.eventType;

    switch (eventType) {
      case 'sign-in':
        return this.createPartialWebPageDetails(`account:${eventType}`, eventType, null, null);
      case 'change-country-flag':
        return this.createPartialWebPageDetails(`account:sign-in:change-country`, 'sign-in', null, 'change-country');
      case 'home':
        return this.createPartialWebPageDetails(`account:${eventType}`, null, null, eventType);
      case 'favorites':
      case 'recents':
        return this.createPartialWebPageDetails(`account:favorites-recents:${eventType}`, 'favorites-recents', eventType, null);
      case 'payments':
        return this.createPartialWebPageDetails(`account:payments-gift-cards:${eventType}`, 'payments-gift-cards', eventType, null);
      case AutoAccrualTrackingType.EnrollInAutoAccrual:
        return this.createPartialWebPageDetails(
          `account:payments-gift-cards:${eventType}`,
          'enroll-in-auto-accrual',
          'payments',
          null,
        );
      case AutoAccrualTrackingType.AutoAccrualInfo:
      case AutoAccrualTrackingType.AutoAccrualOn:
      case AutoAccrualTrackingType.AutoAccrualOff:
      case AutoAccrualTrackingType.AutoAccrualActivated:
      case AutoAccrualTrackingType.AutoAccrualDeactivated:
        return this.createPartialWebPageDetails(
          `account:payments-gift-cards:${eventType}`,
          eventType,
          'payments',
          null,
        );
      case 'add-credit-card':
      case 'add-gift-card':
        return this.createPartialWebPageDetails(`account:payments-gift-cards:${eventType}`, 'payments-gift-cards', null, eventType);
      case 'update-info':
      case 'update-password':
      case 'leave-rewards':
      case 'change-country':
      case 'rewards-add-rewards':
      case 'retro-credit-which-zipcode':
      case 'retro-credit-select-store':
      case 'retro-order-details':
        return this.createPartialWebPageDetails(`account:${eventType}`, null, null, eventType);
      case 'rewards':
      case 'rewards-exchange':
      case 'retro-credit':
      case 'personal-preferences':
        return this.createPartialWebPageDetails(`account:${eventType}:home`, eventType, 'home', null);
      case 'rewards-history':
        return this.createPartialWebPageDetails(`account:rewards-exchange:${eventType}`, 'rewards-exchange', eventType, null);
      case 'rewards-redemption':
        return this.createPartialWebPageDetails(`account:rewards-exchange:${event.reward?.title}`, 'rewards-exchange', null,  event.reward?.title ?? '');
      case PageLoadEventType.GuacModeVerified:
      case PageLoadEventType.VerifyGuacMode:
        return this.createPartialWebPageDetails(`account:verify-guacmode:${eventType}`, 'verify-guacmode', null, eventType);
      case PageLoadEventType.JoinRewards:
        return this.createPartialWebPageDetails(`account:join-rewards:${eventType}`, 'join-rewards', null, eventType);
      case PageLoadEventType.WelcomeToRewards:
        return this.createPartialWebPageDetails(`account:welcome-rewards:${eventType}`, 'welcome-rewards', null, eventType);
      case PageLoadEventType.UnlockGuacMode:
        return this.createPartialWebPageDetails(`account:${eventType}:create-account`, eventType, 'create-account', eventType);
      case PageLoadEventType.GuacModeNewAccount:
        return this.createPartialWebPageDetails(`account:${eventType}:sign-in`, eventType, 'sign-in', eventType);
    }

    return {};
  }

  // Helper function for adding name, subSection, page and subPageName.
  createPartialWebPageDetails(name: string, subSection1: string | null, page: string | null, subPageName: string | null): WebPageDetails {
    let details = { name } as WebPageDetails;

    if (subSection1) {
      details.subSection1 = subSection1;
    }

    if (page) {
      details.page = page;
      details.pageViews = { value: 1 };
    }

    if (subPageName) {
      details.subPageName = subPageName;
      details.subPageViews = { value: 1 };
    }

    return details;
  }
}
